import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HowItWorks from '../components/how-it-works';
import WhyChooseUsBento from '../components/ui/why-choose-us-bento';
import SEO from '../components/SEO';
import forBrandsHero from '../assets/for-brands-hero.png';
import {
  BarChart3,
  Users,
  DollarSign,
  Star,
  Megaphone,
  ShieldCheck,
  FileText,
  TrendingUp,
  Store,
  LayoutGrid,
  Briefcase,
  Lightbulb
} from 'lucide-react';

const ForBrands = () => {
  // Controls for animations
  const controls = useAnimation();

  useEffect(() => {
    controls.start({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    });
  }, [controls]);

  // Benefits data
  const benefits = [
    {
      icon: Users,
      title: "Access to Authentic Influencers",
      description: "Connect with our curated network of genuine influencers across various niches and audience sizes."
    },
    {
      icon: BarChart3,
      title: "Detailed Analytics",
      description: "Track campaign performance with comprehensive analytics and reporting tools."
    },
    {
      icon: Star,
      title: "Quality Content Creation",
      description: "Receive high-quality, authentic content that resonates with your target audience."
    },
    {
      icon: Megaphone,
      title: "Increased Brand Awareness",
      description: "Expand your reach and visibility through authentic influencer partnerships."
    },
    {
      icon: DollarSign,
      title: "Cost-Effective Marketing",
      description: "Maximize your marketing budget with our transparent pricing and measurable results."
    },
    {
      icon: ShieldCheck,
      title: "Brand Safety",
      description: "Our verification process ensures your brand partners with appropriate influencers."
    }
  ];

  // Testimonials
  const testimonials = [
    {
      quote: "Working with Rose at engaged manila has been an excellent experience. She handled our brand, Precious Herbal Pillow, with professionalism and delivered exactly what we expected. The content produced from the influencers generated strong results, approach made the entire process smooth and efficient. She's not only skilled but also very easy to work with—a true partner in every sense.",
      author: "Chester Sy",
      position: "CEO",
      company: "Precious Herbal Pillow"
    },
    {
      quote: "We liked how engaged manila connected us with small influencers who had real engagement. It made a big difference for our brand.",
      author: "Emma Viray",
      position: "Founder",
      company: "Inside the Metro"
    },
    {
      quote: "We tried the platform during its soft launch and were genuinely impressed. The process of creating a campaign and selecting influencers was straightforward, and the support team was very responsive. We’re excited to use it more as the platform grows.",
      author: "Loy Bonaobra",
      position: "Marketing Lead",
      company: "Invictus"
    }
  ];

  return (
    <div className="bg-white min-h-screen relative overflow-hidden">
      <SEO
        title="For Brands — Connect with Influencers for Authentic Reviews"
        description="Launch influencer marketing campaigns on Engaged Manila. Find authentic influencers, manage collaborations, track ROI, and build brand awareness."
        url="/for-brands"
      />
      {/* Background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-15"></div>
        
        {/* Brand-related floating icons */}
        {[TrendingUp, Store, BarChart3, Briefcase, Megaphone, Star].map((Icon, i) => (
          <motion.div
            key={`brand-icon-${i}`}
            className="absolute text-primary opacity-20"
            style={{
              left: `${Math.random() * 90 + 5}%`,
              top: `${Math.random() * 90 + 5}%`,
              fontSize: `${Math.random() * 1 + 0.8}rem`
            }}
            animate={{
              y: [0, Math.random() * 30 - 15],
              rotate: [0, Math.random() * 20 - 10]
            }}
            transition={{
              duration: Math.random() * 8 + 7,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Icon className="w-12 h-12" />
          </motion.div>
        ))}
        
        {/* Decorative circles */}
        <motion.div 
          className="absolute rounded-full bg-primary/10 w-96 h-96 -right-20 top-1/4"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        <motion.div 
          className="absolute rounded-full bg-blue-400/10 w-64 h-64 -left-10 top-2/3"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        {/* Analytics-inspired graph lines */}
        {[...Array(8)].map((_, i) => (
          <motion.div 
            key={`brand-line-${i}`}
            className="absolute bg-primary h-px opacity-20"
            style={{
              width: `${Math.random() * 30 + 10}%`,
              left: `${Math.random() * 90}%`,
              top: `${Math.random() * 100}%`,
              transformOrigin: 'left',
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              repeatType: "reverse",
              delay: i * 0.2
            }}
          />
        ))}
        
        {/* Marketing campaign dots */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={`brand-dot-${i}`}
            className="absolute rounded-full bg-primary"
            style={{
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.2
            }}
            animate={{
              x: [0, Math.random() * 40 - 20],
              y: [0, Math.random() * 40 - 20],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
        ))}
      </div>
      <Header />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-blue-700 pt-24 pb-16 relative overflow-hidden">
        {/* Hero section background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Animated connection network */}
          {[...Array(10)].map((_, i) => (
            <motion.div 
              key={`hero-node-${i}`}
              className="absolute rounded-full bg-white"
              style={{
                width: `${Math.random() * 6 + 4}px`,
                height: `${Math.random() * 6 + 4}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: 0.3
              }}
              animate={{
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{
                duration: Math.random() * 4 + 3,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />
          ))}
          
          {/* Connection lines */}
          {[...Array(8)].map((_, i) => (
            <motion.div 
              key={`hero-line-${i}`}
              className="absolute bg-white h-px"
              style={{
                width: `${Math.random() * 20 + 10}%`,
                left: `${Math.random() * 80}%`,
                top: `${Math.random() * 100}%`,
                transformOrigin: 'left',
                opacity: 0.2
              }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{
                duration: Math.random() * 3 + 2,
                repeat: Infinity,
                repeatType: "reverse",
                delay: i * 0.2
              }}
            />
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <motion.div 
              className="md:w-1/2 md:pr-8"
              initial={{ opacity: 0, y: 20 }}
              animate={controls}
            >
              <h1 className="text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl">
                Grow Your Brand with Authentic Influencer Partnerships
              </h1>
              <p className="mt-3 max-w-md text-base text-blue-100 sm:text-lg md:mt-5 md:text-xl">
                Connect with genuine influencers who will create authentic reviews and content for your products and services
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/register?type=brand"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-primary bg-white hover:bg-gray-100 transition-colors duration-300"
                  >
                    Get Started
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/contact"
                    className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-blue-600 transition-colors duration-300"
                  >
                    Contact Sales
                  </Link>
                </motion.div>
              </div>
            </motion.div>
            <motion.div 
              className="mt-10 md:mt-0 md:w-1/2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
            >
              <img
                src={forBrandsHero}
                alt="Brand marketing meeting"
                className="rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Benefits Section */}
      <WhyChooseUsBento
        eyebrow="Benefits"
        title="Why Brands Choose Us"
        subtitle="Discover how our platform helps brands create authentic connections with their target audience"
        features={benefits.map((b) => ({ title: b.title, description: b.description, icon: b.icon }))}
        stats={[
          { value: "400+", emoji: "🤝", label: "Active Influencers" },
          { value: "500+", emoji: "📊", label: "Campaigns Launched" },
          { value: "98%", emoji: "⭐", label: "Brand Satisfaction" },
        ]}
        illustration={{
          question: "How can I find the right influencers for my brand's product launch?",
          searchLabel: "Find your match",
        }}
      />
      
      {/* How It Works Section */}
      <div id="how-it-works" className="bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Process</h2>
            <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
              How It Works
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Our simple 5-step process to launch successful influencer campaigns
            </p>
          </motion.div>
        </div>

        <HowItWorks
          features={[
            {
              title: "Create Your Brand Profile",
              description: "Sign up and create your brand profile with all relevant information about your company and products.",
              colorTheme: "orange",
            },
            {
              title: "Launch a Campaign",
              description: "Define your campaign objectives, target audience, budget, and requirements for influencers.",
              colorTheme: "blue",
            },
            {
              title: "Review Applications",
              description: "Browse influencer applications and select the best matches for your campaign goals.",
              colorTheme: "purple",
            },
            {
              title: "Approve Content",
              description: "Review and approve influencer content before it goes live to ensure brand alignment.",
              colorTheme: "orange",
            },
            {
              title: "Track Results",
              description: "Monitor campaign performance with real-time analytics and comprehensive reports.",
              colorTheme: "blue",
            },
          ]}
        />
      </div>
      
      {/* Testimonials Section */}
      <div className="py-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="lg:text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Testimonials</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              What Brands Say About Us
            </p>
          </motion.div>
          
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <motion.div 
                key={testimonial.author}
                className="bg-white rounded-lg shadow-md p-6 border border-gray-100"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index, duration: 0.5 }}
                whileHover={{ y: -5 }}
              >
                <div className="flex-1">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="text-base text-gray-700">
                    <p>"{testimonial.quote}"</p>
                  </blockquote>
                  <div className="mt-6">
                    <p className="text-base font-medium text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-500">{testimonial.position}, {testimonial.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="bg-primary">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              <span className="block">Ready to boost your brand?</span>
              <span className="block text-blue-100">Start your first campaign today.</span>
            </h2>
            <p className="mt-4 text-lg leading-6 text-blue-100">
              Join hundreds of brands already growing their business with authentic influencer partnerships.
            </p>
          </motion.div>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <motion.div 
              className="inline-flex rounded-md shadow"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/register?type=brand"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-white hover:bg-gray-50"
              >
                Get started
              </Link>
            </motion.div>
            <motion.div 
              className="ml-3 inline-flex rounded-md shadow"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50"
              >
                View pricing
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="lg:text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">FAQ</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Frequently Asked Questions
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Everything you need to know about working with our platform
            </p>
          </motion.div>
          
          <div className="mt-12 max-w-3xl mx-auto">
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    How do I find the right influencers for my brand?
                  </h3>
                  <div className="mt-2">
                    <p className="text-base text-gray-500">
                      Our platform allows you to filter influencers based on niche, audience demographics, engagement rates, and more. You can also review their previous content to ensure they align with your brand values.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    How much does it cost to run a campaign?
                  </h3>
                  <div className="mt-2">
                    <p className="text-base text-gray-500">
                      Campaign costs vary based on your requirements and the influencers you work with. You set your own budget, and our platform fee is a small percentage of your campaign spend.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    How do I know if my campaign is successful?
                  </h3>
                  <div className="mt-2">
                    <p className="text-base text-gray-500">
                      We provide comprehensive analytics for each campaign, including reach, engagement, click-through rates, and conversions. You can track these metrics in real-time through your brand dashboard.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white shadow overflow-hidden rounded-md">
                <div className="px-6 py-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Do I own the rights to content created by influencers?
                  </h3>
                  <div className="mt-2">
                    <p className="text-base text-gray-500">
                      Content rights are specified in each campaign agreement. Typically, you'll have rights to use the content for your marketing purposes, but the influencer retains copyright. Custom agreements can be arranged for extended usage rights.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            

          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForBrands;
